import React from 'react';
import yaml from 'js-yaml';
import './PreviewPanel.css';
import InventoryPreview from './InventoryPanel/InventoryPreview';
import DialogPreview from './DialogPanel/DialogPreview';
import FloodgateContainer from './FloodgatePreview/FloodgateContainer';

// YAML Highlighting Code BELOW
function getValueClass(value) {
  if (!value || !value.trim()) return null;
  const v = value.trim();
  if (v === 'true' || v === 'false')            return 'yaml-boolean';
  if (v === 'null' || v === '~')                return 'yaml-null';
  if (/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(v)) return 'yaml-number';
  
  // Default all remaining standard text, unquoted text, and >- to string color
  return 'yaml-string'; 
}

function renderValue(value, baseClass) {
  if (!value) return null;
  
  const parts = value.split(/(%[^%]+%|\[\w+\]|\$[A-Z]+)/g);
  
  return parts.map((part, index) => {
    if (!part) return null;
    if (part.startsWith('%') && part.endsWith('%')) {
      return <span key={index} className="cp-placeholder">{part}</span>;
    }
    if (part.startsWith('[') && part.endsWith(']')) {
      return <span key={index} className="cp-command-tag">{part}</span>;
    }
    if (part.startsWith('$') && part === part.toUpperCase()) {
      return <span key={index} className="cp-logic-tag">{part}</span>;
    }
    return <span key={index} className={baseClass}>{part}</span>;
  });
}

function YamlLine({ lineData }) {
  const { line, isMultiline, isSectionGap, isItemTitle } = lineData;
  const gapClass = isSectionGap ? " yaml-section-gap" : "";

  if (!line.trim()) return <div className={`yaml-line yaml-empty${gapClass}`}>&nbsp;</div>;

  if (line.trim().startsWith('#')) {
    return (
      <div className={`yaml-line${gapClass}`}>
        <span className="yaml-comment">{line}</span>
      </div>
    );
  }

  if (isMultiline) {
    const indentMatch = line.match(/^(\s*)(.*)$/);
    return (
      <div className={`yaml-line${gapClass}`}>
        <span className="yaml-indent">{indentMatch[1]}</span>
        {renderValue(indentMatch[2], 'yaml-string')}
      </div>
    );
  }

  // Updated regex to allow quotes ('0':) and spaces ('1, 2':) around keys
  const kvMatch = line.match(/^(\s*)(- )?(['"]?[\w-,\s]+['"]?)(\s*:)(\s*)(.*)$/);
  if (kvMatch) {
    const [, indent, listPart, key, colon, space, value] = kvMatch;
    const valClass = getValueClass(value) || 'yaml-string';
    
    // Dynamically assign Gold (special) to everything, EXCEPT Blue for Item/Layout titles
    const keyClass = isItemTitle ? 'yaml-key' : 'cp-special-key';
    
    return (
      <div className={`yaml-line${gapClass}`}>
        <span>{indent}</span>
        {listPart && <span className="yaml-list">{listPart}</span>}
        <span className={keyClass}>{key}</span>
        <span className="yaml-punctuation">{colon}</span>
        <span>{space}</span>
        {value && renderValue(value, valClass)}
      </div>
    );
  }

  const listMatch = line.match(/^(\s*)(- )(.*)$/);
  if (listMatch) {
    const [, indent, listPart, value] = listMatch;
    const valClass = getValueClass(value) || 'yaml-string';
    return (
      <div className={`yaml-line${gapClass}`}>
        <span>{indent}</span>
        <span className="yaml-list">{listPart}</span>
        {renderValue(value, valClass)}
      </div>
    );
  }

  return (
    <div className={`yaml-line${gapClass}`}>
      {renderValue(line, 'yaml-string')}
    </div>
  );
}

function YamlHighlight({ content }) {
  const lines = content.split('\n');
  let inMultiline = false;
  let multilineBaseIndent = 0;
  
  let currentRootKey = null;
  let isFirstItemInSection = false;
  let itemsIndentLevel = -1; // Allows dynamic detection of 2-space or 4-space indents

  const processedLines = lines.map((line) => {
    const indentMatch = line.match(/^(\s*)/);
    const currentIndent = indentMatch ? indentMatch[1].length : 0;
    let isSectionGap = false;
    let isItemTitle = false;

    // 1. Check if we are exiting a multiline block FIRST
    if (inMultiline) {
      if (line.trim() !== '' && currentIndent <= multilineBaseIndent) {
        inMultiline = false; // We exited! Now fall through to normal key processing for this line.
      } else {
        return { line, isMultiline: true, isSectionGap: false, isItemTitle: false };
      }
    }

    // 2. Normal key processing (now safely handles quotes from js-yaml)
    const kvMatch = line.match(/^(\s*)(- )?(['"]?[\w-,\s]+['"]?)(\s*:)/);
    if (kvMatch && !inMultiline) {
      // Strip quotes to safely check the root key
      const cleanKey = kvMatch[3].replace(/['"]/g, '').trim();

      if (currentIndent === 0) {
        currentRootKey = cleanKey;
        itemsIndentLevel = -1; // Reset expected indent level for new root
        isFirstItemInSection = true;
      } else if (currentRootKey === 'items' || currentRootKey === 'layout') {
        if (itemsIndentLevel === -1) {
          itemsIndentLevel = currentIndent; // Lock in whatever indent spacing the user is using
        }
        
        if (currentIndent === itemsIndentLevel) {
          isItemTitle = true; // Flags this specific line to be colored blue
          if (isFirstItemInSection) {
            isFirstItemInSection = false;
          } else {
            isSectionGap = true; // Add gap to subsequent items
          }
        }
      }
    }

    // 3. Check if this line opens a NEW multiline string
    if (line.match(/(:)(\s*)([>|][+-]?)$/)) {
      inMultiline = true;
      multilineBaseIndent = currentIndent;
    }

    return { line, isMultiline: false, isSectionGap, isItemTitle };
  });

  return (
    <div className="yaml-highlight">
      {processedLines.map((lineData, i) => (
        <YamlLine key={i} lineData={lineData} />
      ))}
    </div>
  );
}
// Yaml Highlight Code END

export default function PreviewPanel({ rawYamlPreview, config, setConfig, textures, selectedItem, setSelectedItem }) {

  const renderPreview = () => {
    const type = config.type || 'inventory';
    switch (type) {
      case 'dialog':
        return <DialogPreview config={config} setConfig={setConfig} textures={textures} selectedItem={selectedItem} />;
      case 'floodgate':
        return <FloodgateContainer config={config} setConfig={setConfig} selectedItem={selectedItem} />;
      case 'inventory':
      default:
        return <InventoryPreview config={config} setConfig={setConfig} textures={textures} selectedItem={selectedItem} setSelectedItem={setSelectedItem} />;
    }
  };

  return (
    <div className="preview-container">
      <div className="preview-content">
        {rawYamlPreview ? (
          <div className="preview-yaml-dump">
            <YamlHighlight content={yaml.dump(config)} />
          </div>
        ) : (
          renderPreview()
        )}
      </div>
    </div>
  );
}
